/**
 * Bundle 55 Bug 4 - sitewide Meta CAPI expired-token banner.
 *
 * Fetches the same health-cards feed the Data Health page uses. If
 * the `meta-token` card is present and severity is red, we render a
 * sticky red banner at the top of every admin page linking to the
 * Pixels rotation UI. The banner does not offer a dismiss action:
 * while the token is invalid, 100 percent of CAPI events are lost,
 * so ignoring the state costs real conversion signal.
 *
 * The banner silently disappears once the token is rotated and the
 * next health-cards refresh confirms it.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';

const REFRESH_MS = 60_000;

const MetaTokenExpiredBanner = () => {
  const [expired, setExpired] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get('/admin/health/cards', { silent401: true });
        if (cancelled) return;
        const cards = Array.isArray(data?.cards) ? data.cards : [];
        const t = cards.find(
          (c) => c && c.id === 'meta-token' && c.severity === 'red',
        );
        if (t) {
          setExpired(true);
          setCount(Number(t.count) || 0);
        } else {
          setExpired(false);
          setCount(0);
        }
      } catch (_) {
        // Silent - admin may not have finished login, or the
        // endpoint 500'd. Banner stays hidden. It will retry on the
        // next interval tick.
      }
    };
    check();
    const iv = setInterval(check, REFRESH_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  if (!expired) return null;

  const label = count > 1
    ? `${count} Meta CAPI tokens are invalid`
    : 'Meta CAPI token is invalid';

  return (
    <div
      role="alert"
      data-testid="meta-token-expired-banner"
      className="sticky top-0 z-50 bg-red-700 text-white shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3 text-sm">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <div className="flex-1 leading-tight">
          <span className="font-semibold">{label}.</span>
          {' '}
          <span className="text-red-100">
            Zero percent of pixel events are landing in Meta right now.
            Rotate the token to restore conversion signal.
          </span>
        </div>
        <Link
          to="/admin/pixels"
          data-testid="meta-token-expired-banner-cta"
          className="inline-flex items-center gap-1 rounded-md bg-white/15 hover:bg-white/25 px-3 py-1.5 text-xs font-semibold transition-colors"
        >
          Rotate token <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
};

export default MetaTokenExpiredBanner;
